<template>
  <nav
    v-if="isAuthenticated"
    class="app-bottom-nav"
    role="navigation"
    :aria-label="$t('nav.mainNavigation')"
  >
    <RouterLink
      v-for="item in items"
      :key="item.name"
      :to="item.route"
      class="app-bottom-nav__item"
      :class="{ 'app-bottom-nav__item--active': isActive(item) }"
      :aria-current="isActive(item) ? 'page' : undefined"
    >
      <span
        class="app-bottom-nav__icon"
        :style="{ '--nav-icon': cssIconUrl(item.icon ?? '') }"
        aria-hidden="true"
      />
      <span class="app-bottom-nav__label">{{ $t(item.label) }}</span>
    </RouterLink>
  </nav>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'

import { BOTTOM_NAVIGATION_NAMES } from '@/shared/config/navigation'
import type { NavigationItem } from '@/shared/types/navigation'
import { useVisibleNavigation } from '@/shared/composables/useVisibleNavigation'
import { cssIconUrl } from '@/shared/utils/assets'
import { useProfileStore } from '@/modules/profile/store/profileStore'

const route = useRoute()
const profileStore = useProfileStore()
const { isAuthenticated } = storeToRefs(profileStore)
const { items: visibleItems } = useVisibleNavigation()

// Пять основных разделов; участнику семьи «Подписку» и «Друзей» не показываем
// (правило R21 — то же, что и в верхней полоске).
const items = computed(() =>
  visibleItems.value.filter((item) => BOTTOM_NAVIGATION_NAMES.includes(item.name)),
)

const isActive = (item: NavigationItem) => route.path.startsWith(item.route)
</script>

<style scoped lang="scss">
// Нижнее меню только на телефоне: пять разделов с иконками, всегда под рукой.
// Шире 768px их показывает верхняя полоска (VpnTabNav).
.app-bottom-nav {
  display: none;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 90;
  background: var(--ds-surface);
  border-top: 1px solid var(--ds-border);
  padding-bottom: env(safe-area-inset-bottom, 0px);

  @media (max-width: 768px) {
    display: flex;
    align-items: stretch;
  }

  &__item {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 8px 4px;
    min-height: 60px;
    color: var(--ds-text-secondary);
    text-decoration: none;
    transition: color 0.15s ease;
    -webkit-tap-highlight-color: transparent;

    &:active {
      background: var(--ds-accent-soft-08);
    }
  }

  &__item--active {
    color: var(--ds-accent);
  }

  // Маска, а не <img>: одноцветная иконка красится текущим цветом и вместе с
  // подписью подсвечивается на активном разделе.
  &__icon {
    width: 24px;
    height: 24px;
    background-color: currentColor;
    mask: var(--nav-icon) no-repeat center / contain;
    -webkit-mask: var(--nav-icon) no-repeat center / contain;
  }

  &__label {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: var(--p-text-11);
    font-weight: var(--ds-weight-medium);
    letter-spacing: 0.01em;
  }
}
</style>
